import React from 'react';
import { useLotImage } from '../../utils/getLotImage';
import { useCommandeData } from './hooks/useCommandeData';
import { useCommandeTabs } from './hooks/useCommandeTabs';
import { useUnreadCounts } from '../../services/messagerie/useUnreadCounts';
import CommandeHeader from './components/CommandeHeader';
import CommandeTabs from './components/CommandeTabs';
import CommandeContent from './components/CommandeContent';
import { scrollToTop } from '../../utils/scroll';
import './SuiviDeCommandeBCV2.css';

// Skeleton plein ecran (chargement) : silhouette banniere + onglets + contenu, dans les
// MEMES conteneurs (.scbc / .scbc__content / .scbc__tab-content) et avec la meme geometrie
// que le vrai rendu -> aucun saut de layout a l'arrivee des donnees. Purement decoratif.
const CommandeBCSkeleton: React.FC = () => (
  <div className="scbc scbc-skeleton" aria-hidden="true">
    <div className="scbc-sk-banner">
      <div className="scbc-sk-back"><span className="scbc-sk scbc-sk-circle" /></div>
      <div className="scbc-sk-h1"><span className="scbc-sk" /></div>
      <div className="scbc-sk-h2"><span className="scbc-sk" /></div>
      <div className="scbc-sk-subtitle"><span className="scbc-sk" /></div>
      <div className="scbc-sk-statusline"><span className="scbc-sk" /></div>
    </div>
    <div className="scbc__content">
      <div className="scbc-sk-tabs">
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className="scbc-sk scbc-sk-tab" />
        ))}
      </div>
      <div className="scbc__tab-content">
        <div className="scbc-sk-content">
          <span className="scbc-sk scbc-sk-block" />
          <span className="scbc-sk scbc-sk-block" />
          <span className="scbc-sk scbc-sk-block scbc-sk-block--lg" />
        </div>
      </div>
    </div>
  </div>
);

// Page PRESTATAIRE commande-bc (v2). Orchestration mince : data -> onglets -> rendu.
const SuiviDeCommandeBCV2: React.FC = () => {
  const { commande, loading, error, refetch } = useCommandeData();
  const lotImage = useLotImage(commande?.operation?.lot_technique?.nom);
  const { descriptors, activeIndex, activeTab, setActiveIndex, requestTab } = useCommandeTabs(commande);
  // Pastille messagerie (badge onglet), cote prestataire BDC. Cle = l'operation parente.
  const { counts: unreadCounts, markRead } = useUnreadCounts('/bdc', 'prestataire', commande?.operation?.id);

  // CGV de session : acceptees une fois, conservees pour les actions signees de l'onglet Offre
  // (acceptation du BC). Remontees ici (parite acheteur SuiviOperationBCV2) ; un refresh les
  // redemande (etat non persiste, par design : CGV a (re)valider a chaque session).
  const [acceptCGV, setAcceptCGV] = React.useState(false);

  // Bascule d'onglet par `kind` (ex. Consultation -> Offre pour la signature/le chiffrage).
  // Bascule d'onglet par `kind`. Remonte en haut de page : les onglets sont longs, sinon on arrive au
  // milieu du nouveau. Point unique pour TOUS les boutons de bascule de la case (la barre d'onglets,
  // elle, est deja en haut).
  const goToTab = React.useCallback(
    (kind: string) => {
      const index = descriptors.findIndex((d) => d.kind === kind && !d.disabled && !d.inactive);
      if (index >= 0) {
        setActiveIndex(index);
        scrollToTop();
      }
    },
    [descriptors, setActiveIndex],
  );

  if (loading) {
    return <CommandeBCSkeleton />;
  }

  if (error && !commande) {
    return (
      <div className="scbc__error">
        <p>Erreur: {error}</p>
        <p>Impossible de charger les détails de cette commande BC.</p>
      </div>
    );
  }

  if (!commande) {
    return <div className="scbc__empty">Aucune commande BC trouvée avec cet identifiant.</div>;
  }

  const numero = commande.operation?.numero ?? commande.id;
  const rang = commande.rang ?? 1;
  const lotTechnique = commande.operation?.lot_technique;
  const departement = commande.operation?.lot_departement?.departement;
  const subtitle =
    lotTechnique && departement
      ? `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`
      : 'Informations de lot non disponibles';

  return (
    <div className="scbc">
      <div className="no-print">
        <CommandeHeader
          titre={commande.operation?.nom ?? 'Commande BC'}
          sousTitre={''}
          subtitle={subtitle}
          commande={commande}
          backgroundImage={lotImage}
        />
      </div>

      <div className="scbc__content">
        <CommandeTabs descriptors={descriptors} activeIndex={activeIndex} onSelect={setActiveIndex} unreadCounts={unreadCounts} />
        <div className="scbc__tab-content">
          <CommandeContent
            commande={commande}
            activeTab={activeTab}
            descriptors={descriptors}
            refetch={refetch}
            goToTab={goToTab}
            requestTab={requestTab}
            acceptCGV={acceptCGV}
            setAcceptCGV={setAcceptCGV}
            onMarkRead={markRead}
          />
        </div>
      </div>
    </div>
  );
};

export default SuiviDeCommandeBCV2;
